import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
    Box,
    Paper,
    Typography,
    Tabs,
    Tab,
    TextField,
    Button,
    Alert,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Chip,
    Card,
    CardContent,
    FormControl,
    Select,
    MenuItem,
    Tooltip,
    Checkbox,
    Autocomplete,
    InputLabel,
    Grid,
    InputAdornment,
    MenuList,
    ClickAwayListener,
    Popper,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,   
} from '@mui/material';
import {
    Delete as DeleteIcon,
    Edit as EditIcon,
    Save as SaveIcon,
    Cancel as CancelIcon,
    Add as AddIcon,
    ArrowBack as ArrowBackIcon,
    Visibility as ViewIcon,
    CloudUpload as UploadIcon,
    Close as CloseIcon,
    CloudUpload as CloudUploadIcon,
    Download as DownloadIcon,
    GetApp as GetAppIcon,
    CheckCircle as CheckIcon,
    Search as SearchIcon,
    Clear as ClearIcon
} from '@mui/icons-material';
import LaboratoryService from '../../../services/laboratoryService';
import { decryptText, decryptAppId } from '../../../utils/encryption';
import { parseEncryptedQuery } from '../../../utils/encryption';
import * as XLSX from 'xlsx';
import {
    insertDANC,
    updateDANCBySourceId,
    getDANC
} from '../../../services/apiCalibration';

import { getScrutinyRemarks, getClauseNo } from '../../../services/api';
import UnfreezeApiService from '../../../services/UnfreezeApiService';

const ReferenceMaterial = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { orgId: urlOrgId } = useParams();

    // Function to get orgId from URL query parameters with decryption
    const getOrgIdFromQuery = useCallback(() => {
        const searchParams = new URLSearchParams(location.search);
        const encryptedOrgId = searchParams.get('orgId');
        if (encryptedOrgId) {
            try {
                const decryptedOrgId = decryptText(encryptedOrgId);
                console.log('📍 ReferenceMaterial - Decrypted orgId from URL query:', decryptedOrgId);
                console.log('📍 ReferenceMaterial - Original encrypted orgId:', encryptedOrgId);
                return decryptedOrgId;
            } catch (error) {
                console.log('⚠️ ReferenceMaterial - Failed to decrypt orgId, using original:', encryptedOrgId, error);
                return encryptedOrgId; // Return original if decryption fails
            }
        }
        return null;
    }, [location.search]);

    // Function to get appId from URL query parameters with decryption
    const getAppIdFromQuery = useCallback(() => {
        const searchParams = new URLSearchParams(location.search);
        const encryptedAppId = searchParams.get('appId');
        if (encryptedAppId) {
            try {
                const decryptedAppId = decryptAppId(encryptedAppId);
                console.log('📍 ReferenceMaterial - Decrypted appId from URL query:', decryptedAppId);
                console.log('📍 ReferenceMaterial - Original encrypted appId:', encryptedAppId);
                return decryptedAppId;
            } catch (error) {
                console.log('⚠️ ReferenceMaterial - Failed to decrypt appId, using original:', encryptedAppId, error);
                return encryptedAppId; // Return original if decryption fails
            }
        }
        return null;
    }, [location.search]);

    // Function to get orgId with priority: isSubUser check > URL query (encrypted) > URL params > null
    const getOrgId = useCallback(() => {


        // First check URL query parameters (encrypted) - highest priority
        const queryOrgId = getOrgIdFromQuery();
        if (queryOrgId) {
            console.log('📍 ReferenceMaterial - Using decrypted orgId from URL query:', queryOrgId);
            return queryOrgId;
        }

        // Then check URL parameters (unencrypted)
        if (urlOrgId) {
            console.log('📍 ReferenceMaterial - Using orgId from URL params:', urlOrgId);
            return urlOrgId;
        }

        // No fallback - orgId must be provided via URL
        console.log('⚠️ ReferenceMaterial - No orgId found in URL parameters');
        return null;
    }, [getOrgIdFromQuery, urlOrgId]);

    // Function to get appId with priority: isSubUser check > URL query (encrypted) only
    const getAppId = useCallback(() => {

        // Only check URL query parameters (encrypted)
        const queryAppId = getAppIdFromQuery();
        if (queryAppId) {
            console.log('📍 ReferenceMaterial - Using decrypted appId from URL query:', queryAppId);
            return queryAppId;
        }

        // No fallback - appId must be provided via URL query parameters
        console.log('⚠️ ReferenceMaterial - No appId found in URL query parameters');
        return null;
    }, [getAppIdFromQuery]);

    // Assessment parameter extraction functions for Assessment users
    const getAssessmentIdFromQuery = () => {
        const searchParams = new URLSearchParams(location.search);
        const encryptedAssessmentId = searchParams.get("assessmentId");
        if (encryptedAssessmentId) {
            try {
                const decryptedAssessmentId = decryptText(encryptedAssessmentId);
                console.log(
                    "📍 Using decrypted assessmentId from URL query:",
                    decryptedAssessmentId
                );
                return decryptedAssessmentId;
            } catch (error) {
                return encryptedAssessmentId;
            }
        }
        return 0;
    };

    const getAssessorIdFromQuery = () => {
        const searchParams = new URLSearchParams(location.search);
        const encryptedAssessorId = searchParams.get("assessorId");
        if (encryptedAssessorId) {
            try {
                const decryptedAssessorId = decryptText(encryptedAssessorId);
                console.log(
                    "📍 Using decrypted assessorId from URL query:",
                    decryptedAssessorId
                );
                return decryptedAssessorId;
            } catch (error) {
                return encryptedAssessorId;
            }
        }
        return 0;
    };

    const getUserTypeId = useCallback(() => {
        const userType = parseInt(localStorage.getItem('userTypeId') || '0');
        console.log('📍 ReferenceMaterial - User Type ID:', userType);
        return userType;
    }, []);

    // Extract and decrypt query parameters
    const queryParams = new URLSearchParams(location.search);
    const { disciplineId, disciplineName, tab } = parseEncryptedQuery(queryParams);

    console.log('🔓 Reference Material - Decrypted Parameters:', {
        disciplineId,
        disciplineName,
        tab
    });

    // State management
    const [activeTab, setActiveTab] = useState(0);

    // If tab parameter is provided, set it directly (for "View Reference" navigation)
    useEffect(() => {
        if (tab !== null && tab !== undefined && tab >= 0 && tab <= 1) {
            setActiveTab(tab);
            console.log('🔓 Reference Material - Setting active tab to:', tab);
        }
    }, [tab]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [referenceMaterials, setReferenceMaterials] = useState([]);
    const [editingMaterial, setEditingMaterial] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [materialToDelete, setMaterialToDelete] = useState(null);
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

    // Bulk upload and preview states
    const [uploadFile, setUploadFile] = useState(null);
    const [previewModalOpen, setPreviewModalOpen] = useState(false);
    const [previewHeaders, setPreviewHeaders] = useState([]);
    const [previewData, setPreviewData] = useState([]);
    const [previewFileName, setPreviewFileName] = useState('');
    const [editedPreviewData, setEditedPreviewData] = useState([]); // 2D array of cell values
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const [colorFilter, setColorFilter] = useState(null);

    // DANC Integration States
    const [dancRecords, setDancRecords] = useState([]);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [adequacyChecks, setAdequacyChecks] = useState({});
    const [applicationStage, setApplicationStage] = useState(null);

    // Adequacy check states
    const [selectedItems, setSelectedItems] = useState([]);
    const [adequacyDialogOpen, setAdequacyDialogOpen] = useState(false);
    const [adequacyStatus, setAdequacyStatus] = useState('');
    const [adequacyRemarks, setAdequacyRemarks] = useState('');

    // Checkbox dropdown states for adequacy
    const [selectedRemarksOptions, setSelectedRemarksOptions] = useState([]);
    const [isCheckboxDropdownOpen, setIsCheckboxDropdownOpen] = useState(false);

    // Scrutiny remarks states
    const [scrutinyRemarks, setScrutinyRemarks] = useState([]);
    const [loadingRemarks, setLoadingRemarks] = useState(false);

    // Clause number states for Assessment users
    const [clauseNo, setClauseNo] = useState([]);
    const [selectedClause, setSelectedClause] = useState(null);

    // Search filter state
    const [searchFilter, setSearchFilter] = useState('');

    // Current user for DANC system - get from auth context or session
    const [currentUser] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem('currentUser')) || 'Admin';
        } catch {
            return 'Admin';
        }
    });

    // Current user ID for DANC system - get numeric ID for Insertedby field
    const [currentUserId] = useState(() => {
        try {
            return parseInt(localStorage.getItem('currentUserId')) || 1;
        } catch {
            return 1;
        }
    });

    // Unfreeze functionality states
    const [unfreezeRecords, setUnfreezeRecords] = useState([]);

    // Form data
    const [formData, setFormData] = useState({
        name: '',
        source: '',
        sourceId: '',
        traceability: '',
        dateOfExpiry: '',
        supportingEntity: ''
    });

    // Get organization and application data from encrypted parameters
    const orgId = getOrgId() || '';
    const appId = getAppId() || '';
    const userId = localStorage.getItem('userId') || '';
    console.log('🔍 ReferenceMaterial - Using orgId:', orgId, 'appId:', appId);

    // Filter reference materials based on search term
    const filteredReferenceMaterials = useMemo(() => {
        let filtered = referenceMaterials;
        
        // Apply color filter if set
        if (colorFilter) {
            filtered = filtered.filter(material => material.color === colorFilter);
        }
        
        // Apply search filter
        if (!searchFilter.trim()) {
            return filtered;
        }

        const searchTerm = searchFilter.toLowerCase();
        return filtered.filter(material => {
            const materialName = (material.MaterialName || material.Name || '').toLowerCase();
            const source = (material.Source || '').toLowerCase();
            const traceability = (material.Traceability || '').toLowerCase();
            const expiry = (material.DateOfExpiry || '').toLowerCase();

            return materialName.includes(searchTerm) ||
                   source.includes(searchTerm) ||
                   traceability.includes(searchTerm) ||
                   expiry.includes(searchTerm);
        });
    }, [referenceMaterials, searchFilter, colorFilter]);

    // Get application stage
    const getApplicationStage = async (appId) => {
        try {
            if (!appId) return;

            const response = await fetch(`${process.env.REACT_APP_API_URL}/laboratory/application-stage/${appId}`);
            const result = await response.json();

            if (result.success) {
                setApplicationStage(result.stage);
            } else {
                console.log('Stage not found, defaulting to stage 1 for demo purposes');
                setApplicationStage(1);
            }
        } catch (error) {
            console.error('Error fetching application stage:', error);
            setApplicationStage(1);
        }
    };

    // Load DA-NC data function
    const loadDANCData = useCallback(async () => {
        if (!orgId || !appId) return;

        try {
            // Determine NCtype based on userTypeId for filtering
            const userTypeId = localStorage.getItem('userTypeId');
            const NCtype = userTypeId === '6' ? 'Assessment' : 'Document Adequacy';
            console.log(`🔍 ReferenceMaterial - Loading DANC data for userType ${userTypeId} with expected NCtype: ${NCtype}`);

            // Get all DA NC records for this org and app
            const response = await getDANC({
                orgid: orgId,
                appid: appId,
                assessorId: assessorId,
                isactive: 1,
            });

            console.log('Reference Material DA-NC API Response:', response);

            if (
                response.data.success &&
                response.data.data &&
                response.data.data.length > 0
            ) {
                // Filter Records for Reference Material section and current user's NCtype
                const referenceMaterialDANCRecords = response.data.data.filter(
                    (record) =>
                        (record.SectionName === 'Reference Material' ||
                            record.SectionName === 'ReferenceMaterial' ||
                            record.SectionName === 'Reference Materials') &&
                        record.NCtype === NCtype
                );

                console.log(`🔍 ReferenceMaterial - Filtered records for NCtype "${NCtype}":`, referenceMaterialDANCRecords.length);

                setDancRecords(referenceMaterialDANCRecords);

                // Create adequacy checks from DA NC data
                const dancAdequacyChecks = {};

                referenceMaterialDANCRecords.forEach((record) => {
                    let sectionKey = record.sourceid; // Use sourceid as the key for reference materials

                    if (sectionKey) {
                        dancAdequacyChecks[sectionKey] = {
                            status: record.InformationStatus || '',
                            remarks: record.InformationProvided || record.Observation || '',
                            isSubmitted: false, // Allow users to modify and resubmit
                            submittedAt: record.updatedate || record.Insertdate,
                            dancRecord: record, // Store original record for reference
                            clauseNo: record.ClauseNo || '', // Include clause number for display
                        };

                        console.log(
                            `Mapped reference material Record to material ID "${sectionKey}":`,
                            {
                                status: record.InformationStatus,
                                remarks: record.InformationProvided || record.Observation,
                                sourceid: record.sourceid,
                            }
                        );
                    }
                });

                // Merge with existing adequacy checks
                setAdequacyChecks((prev) => {
                    const merged = {
                        ...prev,
                        ...dancAdequacyChecks,
                    };
                    console.log('Final merged reference material adequacy checks:', merged);
                    return merged;
                });

                showNotification(
                    `${referenceMaterialDANCRecords.length} reference material Records loaded and bound to adequacy checks`,
                    'success'
                );
            } else {
                console.log('No reference material Records found in response');
                setDancRecords([]);
            }
        } catch (error) {
            console.error('Error loading reference material DA-NC data:', error);
            showNotification('Error loading DA-NC data: ' + error.message, 'error');
        }
    }, [orgId, appId]);

    // Unfreeze functionality: Check if section is unfrozen
    const isSectionUnfrozen = useCallback((sectionName) => {
        if (!Array.isArray(unfreezeRecords)) {
            return false;
        }

        const unfreezeRecord = unfreezeRecords.find(
            record => record.sectionname === sectionName
        );

        if (!unfreezeRecord) {
            return false;
        }

        // Check if unfreeze record is still valid (not expired)
        const expiryDate = new Date(unfreezeRecord.expirydate);
        // Reset expiry date time to start of day for date-only comparison
        expiryDate.setHours(0, 0, 0, 0);
        
        const currentDate = new Date();
        // Reset current date time to start of day for date-only comparison
        currentDate.setHours(0, 0, 0, 0);

        return currentDate <= expiryDate;
    }, [unfreezeRecords]);

    // Load unfreeze records
    const loadUnfreezeRecords = useCallback(async () => {
        const appId = getAppId();
        const organizationId = getOrgId();

        if (!appId || !organizationId) {
            setUnfreezeRecords([]);
            return;
        }

        try {
            const recordsApi = await UnfreezeApiService.getUnfreezeRecords(appId, organizationId);
            const records = recordsApi.data;
            // Ensure records is always an array
            if (Array.isArray(records)) {
                setUnfreezeRecords(records);
            } else {
                setUnfreezeRecords([]);
            }
        } catch (error) {
            console.error('❌ Error loading unfreeze records:', error);
            setUnfreezeRecords([]);
        }
    }, [getAppId, getOrgId]);

    // Memoized unfreeze status to prevent infinite re-renders
    const isReferenceMaterialSectionUnfrozen = useMemo(() => {
        return isSectionUnfrozen('Reference Material');
    }, [isSectionUnfrozen]);

    // Load data when component mounts
    const loadReferenceMaterials = useCallback(async () => {
        setLoading(true);
        try {
            // You'll need to implement this endpoint in your service
            const result = await (localStorage.getItem('userTypeId') === '4' ? LaboratoryService.getReferenceMaterialsWithDeleted(appId, orgId, disciplineId) : LaboratoryService.getReferenceMaterials(appId, orgId, disciplineId));
            if (result.success) {
                setReferenceMaterials(result.data);
            } else {
                setReferenceMaterials([]);
                showNotification('No reference materials found', 'info');
            }
        } catch (error) {
            console.error('Error loading reference materials:', error);
            setReferenceMaterials([]);
            showNotification('Error loading reference materials', 'error');
        } finally {
            setLoading(false);
        }
    }, [appId, orgId, disciplineId]);

    // Fetch clause numbers for Assessment users
    const fetchClauseNo = useCallback(async () => {
        try {
            console.log('Fetching clause numbers for Lab Staff section...');

            const clauseResponse = await getClauseNo({ standard: '2022', feild: 'Medical' });

            if (clauseResponse.data?.success && clauseResponse.data?.data) {
                setClauseNo(clauseResponse.data.data);
                console.log('Loaded clause numbers:', clauseResponse.data.data.length);
            } else {
                console.warn('No clause numbers found or API returned success: false');
                setClauseNo([]);
            }
        } catch (error) {
            console.error('Error fetching clause numbers:', error);
            setClauseNo([]);
        }
    }, []);

    // Fetch clause numbers when component loads for Assessment users
    useEffect(() => {
        if (getUserTypeId() === 6) {
            fetchClauseNo();
        }
    }, [fetchClauseNo, getUserTypeId]);


    // Load reference materials

    useEffect(() => {
        if (disciplineId) {
            loadReferenceMaterials();
        }
    }, [disciplineId, loadReferenceMaterials]);

    // Load application stage and DA-NC data when component mounts
    useEffect(() => {
        if (appId) {
            getApplicationStage(appId);
        }
    }, [appId]);
    // Note: Tab index is now set directly when URL parameters are parsed
    // No need for applicationStage-based adjustment since tabs are always available

    // Load DA-NC data when form data is available
    useEffect(() => {
        if (orgId && appId) {
            loadDANCData();
        }
    }, [orgId, appId, refreshTrigger, loadDANCData]);

    // Load unfreeze records when org and app IDs are available
    useEffect(() => {
        if (getOrgId() && getAppId()) {
            loadUnfreezeRecords();
        }
    }, [loadUnfreezeRecords, getOrgId, getAppId]);

    // Handle form input changes
    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };
    // Show notification
    const showNotification = (message, severity = 'success') => {
        setNotification({ open: true, message, severity });
    };

    // Close notification
    const closeNotification = () => {
        setNotification({ open: false, message: '', severity: 'success' });
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.name || !formData.source || !formData.traceability) {
            showNotification('Please fill in all required fields', 'error');
            return;
        }

        if(!formData.supportingEntity || formData.supportingEntity.trim() === '') {
            showNotification('Please select a supporting entity', 'error');
            return;
        }

        setSubmitting(true);
        try {
            // Debug: Validate decrypted parameters for individual save
            console.log('📝 Reference Material Individual Save - Parameters Check:', {
                appId,
                orgId,
                disciplineId,
                disciplineName,
                userId
            });

            if (!disciplineId) {
                console.error('❌ Reference Material Individual Save - Missing disciplineId!');
                showNotification('Error: Discipline ID is missing. Please navigate back from discipline section.', 'error');
                return;
            }
            const mainOrgId = localStorage.getItem('mainOrgId');

            const materialData = {
                appId: parseInt(appId),
                orgId: parseInt(mainOrgId) || parseInt(orgId),
                disciplineId: parseInt(disciplineId),
                name: formData.name,
                source: formData.source,
                sourceId: formData.sourceId,
                traceability: formData.traceability,
                dateOfExpiry: formData.dateOfExpiry || null,
                supportingEntity: formData.supportingEntity || '',
                createdById: parseInt(userId),
                clientIp: '127.0.0.1' // You may want to get actual IP
            };

            let result;
            if (editingMaterial) {
                result = await LaboratoryService.updateReferenceMaterial(editingMaterial.Id, materialData);
            } else {
                result = await LaboratoryService.addReferenceMaterial(materialData);
            }

            if (result.success) {
                showNotification(
                    editingMaterial ? 'Reference material updated successfully' : 'Reference material added successfully',
                    'success'
                );
                resetForm();
                loadReferenceMaterials();
                setActiveTab(1); // Switch to view tab
            } else {
                showNotification(result.message || 'Failed to save reference material', 'error');
            }
        } catch (error) {
            console.error('Error saving reference material:', error);
            showNotification('Error saving reference material', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    // Reset form
    const resetForm = () => {
        setFormData({
            name: '',
            source: '',
            sourceId: '',
            traceability: '',
            dateOfExpiry: '',
            supportingEntity: ''
        });
        setEditingMaterial(null);
    };

    // Handle edit
    const handleEdit = (material) => {
        setFormData({
            name: material.Name || '',
            source: material.Source || '',
            sourceId: material.SourceId || '',
            traceability: material.Traceability || '',
            dateOfExpiry: material.DateOfExpiry && material.DateOfExpiry !== ''
                ? material.DateOfExpiry.split('T')[0] // Extract just the date part
                : '',
            supportingEntity: material.supportingEntity || ''    
        });
        setEditingMaterial(material);
        setActiveTab(0); // Switch to add tab for editing
    };

    // Handle delete
    const handleDelete = (material) => {
        setMaterialToDelete(material);
        setDeleteDialogOpen(true);
    };

    // Confirm delete
    const confirmDelete = async () => {
        if (!materialToDelete) return;

        try {
            const result = await LaboratoryService.deleteReferenceMaterial(materialToDelete.Id);
            if (result.success) {
                showNotification('Reference material deleted successfully', 'success');
                loadReferenceMaterials();
            } else {
                showNotification(result.message || 'Failed to delete reference material', 'error');
            }
        } catch (error) {
            console.error('Error deleting reference material:', error);
            showNotification('Error deleting reference material', 'error');
        } finally {
            setDeleteDialogOpen(false);
            setMaterialToDelete(null);
        }
    };

    // Cancel delete
    const cancelDelete = () => {
        setDeleteDialogOpen(false);
        setMaterialToDelete(null);
    };

    // Handle tab change
    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        if (newValue === 0) {
            resetForm();
        }
    };

    // Navigate back
    const handleBack = () => {
        navigate(`/main/medical-ppp-ffl/discipline?orgId=${encodeURIComponent(queryParams.get('orgId') || '')}&appId=${encodeURIComponent(queryParams.get('appId') || '')}`);
    };

    // Download template functions
    const downloadEmptyTemplate = () => {
        const headers = ['Reference Material Name', 'Source', 'Source ID', 'Traceability', 'Date of Expiry'];
        const sampleData = [
            ['Sample Reference Material 1', 'Sample Source 1', 'ID001', 'Sample Traceability 1', '2025-12-31'],
            ['Sample Reference Material 2', 'Sample Source 2', 'ID002', 'Sample Traceability 2', '2026-06-30']
        ];

        downloadTemplate(headers, sampleData, 'reference_materials_template.xlsx');
    };

    const downloadExistingDataTemplate = () => {
        const headers = ['Reference Material Name', 'Source', 'Source ID', 'Traceability', 'Date of Expiry'];
        const existingData = referenceMaterials.map(material => [
            material.Name || '',
            material.Source || '',
            material.SourceId || '',
            material.Traceability || '',
            material.DateOfExpiry && material.DateOfExpiry !== ''
                ? material.DateOfExpiry.split('T')[0]
                : ''
        ]);

        downloadTemplate(headers, existingData, 'existing_reference_materials.xlsx');
    };

    const downloadTemplate = (headers, data, filename) => {
        try {
            // Create workbook and worksheet
            const workbook = XLSX.utils.book_new();
            const worksheetData = [headers, ...data];
            const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

            // Set column widths for better readability
            const columnWidths = headers.map(() => ({ wch: 25 }));
            worksheet['!cols'] = columnWidths;

            // Style the header row
            const headerRange = XLSX.utils.decode_range(worksheet['!ref']);
            for (let col = headerRange.s.c; col <= headerRange.e.c; col++) {
                const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
                if (!worksheet[cellAddress]) continue;

                // Add basic styling (note: this is limited in XLSX library)
                worksheet[cellAddress].s = {
                    font: { bold: true },
                    fill: { fgColor: { rgb: "E3F2FD" } }
                };
            }

            // Add worksheet to workbook
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Reference Materials');

            // Save file
            XLSX.writeFile(workbook, filename);

            showNotification(`Template downloaded successfully: ${filename}`, 'success');
        } catch (error) {
            console.error('Error downloading template:', error);
            showNotification('Error downloading template', 'error');
        }
    };

    // Handle file upload
    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            const fileType = file.name.split('.').pop().toLowerCase();
            if (['xlsx', 'xls', 'csv'].includes(fileType)) {
                setUploadFile(file);
                setPreviewHeaders([]);
                setPreviewData([]);
                setEditedPreviewData([]);
                setValidationErrors([]);
            } else {
                showNotification('Please select a valid Excel (.xlsx, .xls) or CSV file', 'error');
            }
        }
    };

    // Handle preview data
    const handlePreviewData = async () => {
        if (!uploadFile) {
            showNotification('Please select a file first', 'error');
            return;
        }

        try {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    let data;
                    const fileType = uploadFile.name.split('.').pop().toLowerCase();

                    if (fileType === 'csv') {
                        // Parse CSV
                        const text = e.target.result;
                        const lines = text.split('\n').filter(line => line.trim());
                        if (lines.length === 0) {
                            showNotification('The CSV file is empty', 'error');
                            return;
                        }

                        const headers = lines[0].split(',').map(h => h.trim().replace(/['"]/g, ''));
                        const rows = lines.slice(1).map(line => {
                            const values = line.split(',').map(v => v.trim().replace(/['"]/g, ''));
                            const row = {};
                            headers.forEach((header, index) => {
                                row[header] = values[index] || '';
                            });
                            return row;
                        });
                        data = { headers, rows };
                    } else {
                        // Parse Excel
                        const workbook = XLSX.read(e.target.result, { type: 'binary' });
                        const sheetName = workbook.SheetNames[0];
                        const worksheet = workbook.Sheets[sheetName];
                        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

                        if (jsonData.length === 0) {
                            showNotification('The Excel file is empty', 'error');
                            return;
                        }

                        const headers = jsonData[0] || [];
                        const rows = jsonData.slice(1).map(row => {
                            const rowObj = {};
                            headers.forEach((header, index) => {
                                rowObj[header] = row[index] || '';
                            });
                            return rowObj;
                        });
                        data = { headers, rows };
                    }

                    if (data.rows.length > 1000) {
                        showNotification('File contains too many rows. Maximum 1000 rows allowed.', 'error');
                        return;
                    }

                    // Validate required columns
                    const requiredColumns = ['Reference Material Name', 'Source', 'Traceability', 'Date of Expiry'];
                    const optionalColumns = ['Source ID'];
                    const missingColumns = requiredColumns.filter(col => !data.headers.includes(col));

                    if (missingColumns.length > 0) {
                        showNotification(
                            `Missing required columns: ${missingColumns.join(', ')}. Please check your file format.`,
                            'error'
                        );
                        return;
                    }

                    setPreviewHeaders(data.headers);
                    setPreviewData(data.rows);
                    setEditedPreviewData(data.rows.map(row => ({ ...row })));
                    setPreviewFileName(uploadFile.name);
                    setValidationErrors([]);
                    setPreviewModalOpen(true);

                } catch (error) {
                    console.error('Error parsing file:', error);
                    showNotification('Error reading file. Please check the file format.', 'error');
                }
            };

            if (uploadFile.name.endsWith('.csv')) {
                reader.readAsText(uploadFile);
            } else {
                reader.readAsBinaryString(uploadFile);
            }
        } catch (error) {
            console.error('Error processing file:', error);
            showNotification('Error processing file', 'error');
        }
    };

    // Handle preview modal close
    const handlePreviewModalClose = () => {
        if (hasUnsavedChanges) {
            if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
                setPreviewModalOpen(false);
                setHasUnsavedChanges(false);
            }
        } else {
            setPreviewModalOpen(false);
        }
    };

    // Handle cell edit in preview
    const handlePreviewCellEdit = (rowIndex, column, value) => {
        const newData = [...editedPreviewData];
        newData[rowIndex][column] = value;
        setEditedPreviewData(newData);
        setHasUnsavedChanges(true);

        // Clear validation error for this cell
        const newErrors = validationErrors.filter(
            error => !(error.row === rowIndex && error.column === column)
        );
        setValidationErrors(newErrors);
    };

    // Validate preview data
    const validatePreviewData = () => {
        const errors = [];
        const requiredFields = ['Reference Material Name', 'Source', 'Traceability', 'Date of Expiry'];

        editedPreviewData.forEach((row, index) => {
            requiredFields.forEach(field => {
                if (!row[field] || row[field].toString().trim() === '') {
                    errors.push({
                        row: index,
                        column: field,
                        message: 'This field is required'
                    });
                }
            });

            // Validate date format
            if (row['Date of Expiry']) {
                const datePattern = /^\d{4}-\d{2}-\d{2}$/;
                if (!datePattern.test(row['Date of Expiry'])) {
                    errors.push({
                        row: index,
                        column: 'Date of Expiry',
                        message: 'Date must be in YYYY-MM-DD format'
                    });
                }
            }
        });

        setValidationErrors(errors);
        return errors.length === 0;
    };

    // Save bulk upload data
    const handleSaveBulkData = async () => {
        if (!validatePreviewData()) {
            showNotification('Please fix validation errors before saving', 'error');
            return;
        }

        try {
            const applicationId = appId;
            const userId = localStorage.getItem('userId') || '';

            // Debug: Validate decrypted parameters for bulk save
            console.log('📝 Reference Material Bulk Save - Parameters Check:', {
                appId: applicationId,
                orgId,
                disciplineId,
                disciplineName,
                userId
            });

            if (!disciplineId) {
                console.error('❌ Reference Material Bulk Save - Missing disciplineId!');
                showNotification('Error: Discipline ID is missing. Please navigate back from discipline section.', 'error');
                return;
            }

            const bulkData = editedPreviewData.map(row => ({
                name: row['Reference Material Name'],
                source: row['Source'],
                sourceId: row['Source ID'] || '',
                traceability: row['Traceability'],
                dateOfExpiry: row['Date of Expiry'] || null,
                applicationId: parseInt(applicationId),
                disciplineId: parseInt(disciplineId),
                createdById: parseInt(userId),
                clientIp: '127.0.0.1'
            }));

            const result = await LaboratoryService.bulkAddReferenceMaterials(bulkData);

            if (result.success) {
                showNotification(`Successfully added ${bulkData.length} reference materials`, 'success');
                setPreviewModalOpen(false);
                setUploadFile(null);
                setHasUnsavedChanges(false);
                loadReferenceMaterials();
                setActiveTab(1); // Switch to view tab
            } else {
                showNotification(result.message || 'Failed to save reference materials', 'error');
            }
        } catch (error) {
            console.error('Error saving bulk data:', error);
            showNotification('Error saving data', 'error');
        }
    };

    // Helper function to get adequacy status for a reference material
    const getMaterialStatus = (materialId) => {
        return adequacyChecks[materialId] || { status: '', remarks: '' };
    };

    // Checkbox handlers for adequacy
    const handleSelectItem = (itemId) => {
        setSelectedItems(prev => {
            if (prev.includes(itemId)) {
                return prev.filter(id => id !== itemId);
            } else {
                return [...prev, itemId];
            }
        });
    };

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            // Select all items in filtered list, but preserve existing selections from other filters
            const filteredIds = filteredReferenceMaterials.map(item => item.Id);
            const existingUnfilteredSelections = selectedItems.filter(id => 
                !referenceMaterials.some(material => material.Id === id)
            );
            setSelectedItems([...existingUnfilteredSelections, ...filteredIds]);
        } else {
            // Deselect all filtered items, but preserve selections from other filters
            const filteredIds = filteredReferenceMaterials.map(item => item.Id);
            setSelectedItems(selectedItems.filter(id => !filteredIds.includes(id)));
        }
    };

    // Get row styling based on color field for userTypeId 4
    const getRowStyling = (material) => {
        const userTypeId = localStorage.getItem('userTypeId');
        
        // Only apply color coding for userTypeId = 4
        if (userTypeId !== '4') {
            return {
                '&:hover': {
                    backgroundColor: '#f8fafc'
                },
                '& .MuiTableCell-root': {
                    borderBottom: '1px solid #e2e8f0',
                    py: 2
                }
            };
        }

        // Color mapping based on material.color field
        const colorMap = {
            'Blue': {
                backgroundColor: '#bbdefb !important',
                color: '#0d47a1',
                '&:hover': { backgroundColor: '#90caf9 !important' },
                '& .MuiTableCell-root': {
                    borderBottom: '1px solid #64b5f6',
                    py: 2,
                    backgroundColor: 'transparent',
                    color: '#0d47a1',
                    fontWeight: 500
                }
            },
            'Black': {
                backgroundColor: 'inherit',
                color: 'inherit',
                '&:hover': { backgroundColor: '#f8fafc !important' },
                '& .MuiTableCell-root': {
                    borderBottom: '1px solid #e2e8f0',
                    py: 2,
                    backgroundColor: 'transparent'
                }
            },
            'Red': {
                backgroundColor: '#ffcdd2 !important',
                color: '#b71c1c',
                '&:hover': { backgroundColor: '#ef9a9a !important' },
                '& .MuiTableCell-root': {
                    borderBottom: '1px solid #e57373',
                    py: 2,
                    backgroundColor: 'transparent',
                    color: '#b71c1c',
                    fontWeight: 500
                }
            },
            'Orange': {
                backgroundColor: '#ffe0b2 !important',
                color: '#e65100',
                '&:hover': { backgroundColor: '#ffcc80 !important' },
                '& .MuiTableCell-root': {
                    borderBottom: '1px solid #ffb74d',
                    py: 2,
                    backgroundColor: 'transparent',
                    color: '#e65100',
                    fontWeight: 500
                }
            }
        };

        return colorMap[material.color] || colorMap['Black'];
    };

    // Adequacy popup handlers
    const handleMarkAdequate = () => {
        if (selectedItems.length === 0) {
            showNotification('Please select at least one reference material to mark as adequate', 'warning');
            return;
        }
        setAdequacyDialogOpen(true);
        fetchScrutinyRemarks();
    };

    const fetchScrutinyRemarks = async () => {
        setLoadingRemarks(true);
        try {
            const responseApi = await getScrutinyRemarks({
                sectionName: 'Reference Material',
                type: localStorage.getItem('userTypeId') === '6' ? 'Assessment' : 'Document Review'
            });
            const response = responseApi.data;
            if (response.success && response.data) {
                setScrutinyRemarks(response.data);
                console.log('Reference Material: Loaded scrutiny remarks:', response.data.length);
            } else {
                setScrutinyRemarks([]);
            }
        } catch (error) {
            console.error('Error fetching scrutiny remarks:', error);
            setScrutinyRemarks([]);
        } finally {
            setLoadingRemarks(false);
        }
    };

    const assessmentId = getAssessmentIdFromQuery();
    const assessorId = getAssessorIdFromQuery();

    const closeAdequacyDialog = () => {
        setAdequacyDialogOpen(false);
        setAdequacyStatus('');
        setAdequacyRemarks('');
        setScrutinyRemarks([]);
        setSelectedClause(null); // Clear selected clause when dialog closes
        // Reset checkbox dropdown states
        setSelectedRemarksOptions([]);
        setIsCheckboxDropdownOpen(false);
    };

    // Checkbox dropdown handlers for adequacy
    const handleCheckboxChange = useCallback((option, isChecked) => {
        const optionText = typeof option === 'string' ? option : option.inadequate_remarks;
        
        if (isChecked) {
            setSelectedRemarksOptions(prev => [...prev, option]);
        } else {
            setSelectedRemarksOptions(prev => 
                prev.filter(item => {
                    const itemText = typeof item === 'string' ? item : item.inadequate_remarks;
                    return itemText !== optionText;
                })
            );
        }
    }, []);

    const handleClearAllCheckboxes = useCallback(() => {
        setSelectedRemarksOptions([]);
        setAdequacyRemarks('');
    }, []);

    const handleAdequacyRemarksChange = useCallback((newValue, isFromDropdown = false) => {
        setAdequacyRemarks(newValue);
        
        if (!isFromDropdown && selectedRemarksOptions.length > 0) {
            const selectedTexts = selectedRemarksOptions.map(option => 
                typeof option === 'string' ? option : option.inadequate_remarks
            );
            
            if (!selectedTexts.some(text => newValue.includes(text))) {
                setSelectedRemarksOptions([]);
            }
        }
    }, [selectedRemarksOptions]);

    // Update remarks when checkbox selections change
    useEffect(() => {
        if (selectedRemarksOptions.length > 0) {
            const selectedTexts = selectedRemarksOptions.map(option => 
                typeof option === 'string' ? option : option.inadequate_remarks
            );
            
            const currentText = adequacyRemarks || '';
            const existingRemarks = currentText.split(',').map(s => s.trim()).filter(Boolean);
            
            const newRemarks = [...new Set([...existingRemarks, ...selectedTexts])];
            const updatedText = newRemarks.join(', ');
            
            if (updatedText !== currentText) {
                setAdequacyRemarks(updatedText);
            }
        }
    }, [selectedRemarksOptions, adequacyRemarks]);

    const handleAdequacySubmit = async () => {
        if (!adequacyStatus ||
            (adequacyStatus === 'Inadequate' && !adequacyRemarks?.trim()) ||
            (adequacyStatus === 'Inadequate' && getUserTypeId() === 6 && !selectedClause)) {
            showNotification(
                getUserTypeId() === 6 && adequacyStatus === 'Inadequate' && !selectedClause
                    ? 'Please select a clause number for Assessment inadequate status'
                    : 'Please fill all required fields',
                'error'
            );
            return;
        }

        setLoading(true);
        try {
     
            let successCount = 0;
            let errorCount = 0;
            const errors = [];

            // Process each selected reference material
            for (const materialId of selectedItems) {
                try {
                    // Check if Record exists for this reference material
                    const existingRecord = dancRecords.find(
                        (record) =>
                            record.sourceid && parseInt(record.sourceid) === parseInt(materialId)
                    );

                    if (existingRecord) {
                         const userTypeIdString = localStorage.getItem('userTypeId');
                        const NCtype = userTypeIdString === '4' ? 'Document Adequacy' : 'Assessment';
                        // Update existing Record
                        const baseUpdateData = {
                            subsectionName: 'Reference Material Details',
                            informationStatus: adequacyStatus,
                            observation:
                                adequacyStatus === 'Inadequate'
                                    ? adequacyRemarks || 'Document adequacy marked as Inadequate'
                                    : 'Document adequacy check completed',
                                    NCtype: NCtype,
                            sourceid: materialId,
                            isactive: 1,
                        };

                        const response = await updateDANCBySourceId(
                            orgId,
                            appId,
                            'Reference Material',
                            materialId,
                            assessmentId || 0,
                            assessorId || localStorage.getItem('orgId'),
                            baseUpdateData
                        );

                        if (response.success) {
                            console.log(`✅ Record updated for material ${materialId}`);
                            successCount++;
                        } else {
                            throw new Error(response.message || 'Failed to Update');
                        }
                    } else {
                        // Create new Record with userTypeId-based NCtype
                        const userTypeId = getUserTypeId();
                        const userTypeIdString = localStorage.getItem('userTypeId');
                        const NCtype = userTypeIdString === '4' ? 'Document Adequacy' : 'Assessment';
                        console.log(`🔍 ReferenceMaterial - Creating DANC record for userType ${userTypeIdString} with NCtype: ${NCtype}`);

                        const dancData = {
                            Orgid: parseInt(getOrgId()),
                            Appid: parseInt(getAppId()),
                            SectionName: 'Reference Material',
                            SubsectionName: 'Reference Material Details',
                            InformationProvided: adequacyRemarks || '',
                            InformationStatus: adequacyStatus,
                            Observation:
                                adequacyStatus === 'Inadequate'
                                    ? adequacyRemarks || 'Document adequacy marked as Inadequate'
                                    : 'Document adequacy check completed',
                            sourceid: materialId,
                            Insertedby: currentUserId,
                            ClauseNo: selectedClause?.ClauseNo || selectedClause?.DisplayName || selectedClause,
                            assessmentId: parseInt(assessmentId) || 0,
                            assessorId: parseInt(assessorId) || localStorage.getItem('orgId'),
                            NCtype: NCtype,
                        };

                        console.log(`Creating Record for material ${materialId}:`, dancData);
                        const response = await insertDANC(dancData);

                        if (response.success) {
                            console.log(`✅ Record created for material ${materialId}`);
                            successCount++;
                        } else {
                            throw new Error(response.message || 'Failed to create Record');
                        }
                    }

                    // Update adequacy checks locally and include clause info for Assessment users
                    const adequacyUpdate = {
                        status: adequacyStatus,
                        remarks: adequacyRemarks,
                        isSubmitted: true,
                        submittedAt: new Date().toISOString(),
                    };

                    // Add clause number for Assessment users
                    if (getUserTypeId() === 6 && selectedClause) {
                        adequacyUpdate.clauseNo = selectedClause?.ClauseNo || selectedClause?.DisplayName || selectedClause;
                        console.log('📍 ReferenceMaterial - Storing clause number in adequacy check:', adequacyUpdate.clauseNo);
                    }

                    setAdequacyChecks((prev) => ({
                        ...prev,
                        [materialId]: adequacyUpdate,
                    }));
                } catch (error) {
                    console.error(`Error processing material ${materialId}:`, error);
                    errors.push(`Material ${materialId}: ${error.message}`);
                    errorCount++;
                }
            }

            // Show results
            if (successCount > 0) {
                showNotification(
                    `Adequacy check completed: ${successCount} reference material(s) processed successfully${errorCount > 0 ? `, ${errorCount} failed` : ''
                    }`,
                    errorCount > 0 ? 'warning' : 'success'
                );
            }

            if (errorCount > 0 && errors.length > 0) {
                console.error('Reference material adequacy submission errors:', errors);
            }

            // Trigger refresh to reload DA-NC data
            setRefreshTrigger((prev) => prev + 1);

            // Clear selections and close dialog
            setSelectedItems([]);
            closeAdequacyDialog();
        } catch (error) {
            console.error('Error submitting reference material adequacy check:', error);
            showNotification('Error submitting adequacy check: ' + error.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
      <Box sx={{ p: 3, backgroundColor: "#f8fafc", minHeight: "100vh" }}>
        {/* Header */}
        <Paper
          sx={{
            mb: 3,
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            borderRadius: 2,
          }}
        >
          <Box
            sx={{
              py: 2,
              px: 3,
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
              borderRadius: "8px 8px 0 0",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <IconButton
                onClick={handleBack}
                sx={{
                  color: "white",
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
                }}
              >
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                🧪 Reference Materials (4.2)
              </Typography>
              {isReferenceMaterialSectionUnfrozen && (
                <Chip
                  label="UNFROZEN"
                  size="small"
                  sx={{
                    backgroundColor: "#ff9800",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "0.75rem",
                    animation: "pulse 2s infinite",
                  }}
                />
              )}
            </Box>
            <Chip
              label={disciplineName || "Unknown Discipline"}
              sx={{
                backgroundColor: "rgba(255,255,255,0.2)",
                color: "white",
                fontWeight: 500,
              }}
            />
          </Box>

          {/* Tabs */}
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              px: 3,
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 500,
                fontSize: "1rem",
                minHeight: 56,
              },
            }}
          >
            {(applicationStage === null ||
              applicationStage === "" ||
              applicationStage === "18a" ||
              applicationStage === "18" ||
              applicationStage === "18b" ||
              applicationStage === "20" ||
              isReferenceMaterialSectionUnfrozen) && (
              <Tab
                label="Add Reference"
                icon={<AddIcon />}
                iconPosition="start"
              />
            )}
            <Tab
              label="View Existing"
              icon={<ViewIcon />}
              iconPosition="start"
            />
          </Tabs>
        </Paper>

        {/* Unfreeze Alert */}
        {isReferenceMaterialSectionUnfrozen && (
          <Alert
            severity="warning"
            sx={{
              mb: 2,
              "& .MuiAlert-icon": { alignItems: "center" },
              backgroundColor: "#fff3cd",
              borderColor: "#ffeaa7",
              color: "#856404",
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              🔓 This Reference Material section has been unfrozen and is
              temporarily editable.
            </Typography>
          </Alert>
        )}

        {/* Tab Content */}
        {(applicationStage === null ||
          applicationStage === "" ||
          applicationStage === "18a" ||
          applicationStage === "18" ||
          applicationStage === "18b" ||
          applicationStage === "20" ||
          isReferenceMaterialSectionUnfrozen) &&
          activeTab === 0 && (
            <Paper
              sx={{
                p: 4,
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                borderRadius: 2,
              }}
            >
              <Typography
                variant="h6"
                sx={{ mb: 3, color: "#334155", fontWeight: 600 }}
              >
                {editingMaterial
                  ? "Edit Reference Material"
                  : "Add New Reference Material"}
              </Typography>

              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  {/* Name of Calibrator/Standard/Strain */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      label="Name of Calibrator/Standard/Strain (Reference material)"
                      value={formData.name}
                      onChange={(e) =>
                        handleInputChange("name", e.target.value)
                      }
                      variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "white",
                          "&:hover": {
                            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                          },
                        },
                      }}
                    />
                  </Grid>

                  {/* Source */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      label="Source"
                      value={formData.source}
                      onChange={(e) =>
                        handleInputChange("source", e.target.value)
                      }
                      variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "white",
                          "&:hover": {
                            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                          },
                        },
                      }}
                    />
                  </Grid>

                  {/* Traceability */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      label="Traceability"
                      value={formData.traceability}
                      onChange={(e) =>
                        handleInputChange("traceability", e.target.value)
                      }
                      variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "white",
                          "&:hover": {
                            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                          },
                        },
                      }}
                    />
                  </Grid>

                  {/* Date of Expiry */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Date of Expiry"
                      type="date"
                      value={formData.dateOfExpiry || ""}
                      onChange={(e) =>
                        handleInputChange("dateOfExpiry", e.target.value)
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "white",
                          "&:hover": {
                            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                          },
                        },
                      }}
                    />
                  </Grid>

                  {/* Indicate supporting entity employment */}
                  <Grid item xs={12}>
                    <FormControl component="fieldset">
                      <FormLabel
                        component="legend"
                        sx={{ fontWeight: 400, mb: 1 }}
                      >
                        Does this reference material belong to an operational entity or a supporting entity?*
                      </FormLabel>
                      <RadioGroup
                        row
                        value={formData.supportingEntity}
                        onChange={(e) =>
                          handleInputChange("supportingEntity", e.target.value)
                        }
                      >
                        <FormControlLabel value="Operational" control={<Radio />} label="Operational" />
                        <FormControlLabel value="Supporting" control={<Radio />} label="Supporting" />
                      </RadioGroup>
                    </FormControl>
                  </Grid>

                  {/* Action Buttons */}
                  <Grid item xs={12}>
                    {(applicationStage === null ||
                      applicationStage === "" ||
                      applicationStage === "18a" ||
                      isReferenceMaterialSectionUnfrozen) && (
                      <Box
                        sx={{
                          display: "flex",
                          gap: 2,
                          justifyContent: "flex-end",
                          mt: 2,
                        }}
                      >
                        <Button
                          variant="outlined"
                          onClick={resetForm}
                          startIcon={<CancelIcon />}
                          disabled={submitting}
                        >
                          {editingMaterial ? "Cancel Edit" : "Reset"}
                        </Button>
                        <Button
                          type="submit"
                          variant="contained"
                          startIcon={
                            submitting ? (
                              <CircularProgress size={20} />
                            ) : (
                              <SaveIcon />
                            )
                          }
                          disabled={submitting}
                          sx={{
                            background:
                              "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                            "&:hover": {
                              background:
                                "linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)",
                            },
                          }}
                        >
                          {submitting
                            ? "Saving..."
                            : editingMaterial
                              ? "Update Material"
                              : "Add Material"}
                        </Button>
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </form>
            </Paper>
          )}

        {(((applicationStage === null ||
          applicationStage === "" ||
          applicationStage === "18a" ||
          applicationStage === "18" ||
          applicationStage === "18b" ||
          applicationStage === "20" ||
          isReferenceMaterialSectionUnfrozen) &&
          activeTab === 1) ||
          (applicationStage !== null &&
            applicationStage !== "" &&
            applicationStage !== "18" &&
            applicationStage !== "18a" &&
            applicationStage !== "18b" &&
            applicationStage !== "20" &&
            !isReferenceMaterialSectionUnfrozen &&
            activeTab === 0)) && (
          <Paper
            sx={{
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                py: 3,
                px: 4,
                background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                borderBottom: "1px solid rgba(0,0,0,0.06)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: "#334155",
                      fontSize: "1.125rem",
                    }}
                  >
                    📋 Existing Reference Materials (
                    {filteredReferenceMaterials.length} of{" "}
                    {referenceMaterials.length})
                  </Typography>
                  <TextField
                    size="small"
                    placeholder="Search reference materials..."
                    value={searchFilter}
                    onChange={(e) => setSearchFilter(e.target.value)}
                    sx={{
                      width: 300,
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "white",
                        borderRadius: 1.5,
                        "&:hover": {
                          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        },
                        "&.Mui-focused": {
                          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                        },
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon
                            sx={{ color: "text.secondary", fontSize: "1.1rem" }}
                          />
                        </InputAdornment>
                      ),
                      endAdornment: searchFilter && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => setSearchFilter("")}
                            sx={{ p: 0.5 }}
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                      sx: {
                        fontSize: "0.875rem",
                      },
                    }}
                  />
                </Box>
                {(applicationStage === "18" ||
                  applicationStage === "18b" ||
                  applicationStage === "20") &&
                  (localStorage.getItem("userTypeId") === "4" ||
                    localStorage.getItem("userTypeId") === "6") &&
                  selectedItems.length > 0 && (
                    <Button
                      variant="contained"
                      color="success"
                      onClick={handleMarkAdequate}
                      sx={{
                        px: 3,
                        py: 1,
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 600,
                      }}
                    >
                      Mark Adequate ({selectedItems.length})
                    </Button>
                  )}
              </Box>
            </Box>

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress />
              </Box>
            ) : referenceMaterials.length === 0 ? (
              <Box sx={{ p: 4, textAlign: "center" }}>
                <Typography variant="body1" color="text.secondary">
                  No reference materials found. Add some using the "Add
                  Reference" tab.
                </Typography>
              </Box>
            ) : filteredReferenceMaterials.length === 0 ? (
              <Box sx={{ p: 4, textAlign: "center" }}>
                <Typography variant="body1" color="text.secondary">
                  No reference materials match your search "{searchFilter}".
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  Try adjusting your search terms or clear the search to view
                  all materials.
                </Typography>
              </Box>
            ) : (
              <>
                {/* Color Legend - Only show for userTypeId 4 */}
                {localStorage.getItem("userTypeId") === "4" && (
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      mb: 2,
                      p: 1.5,
                      backgroundColor: "#f8fafc",
                      borderRadius: 1,
                      border: "1px solid #e2e8f0",
                      flexWrap: "wrap",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600, mr: 1 }}>
                      Legend:
                    </Typography>
                    <Chip
                      label="New"
                      size="small"
                      onClick={() =>
                        setColorFilter(colorFilter === "Blue" ? null : "Blue")
                      }
                      sx={{
                        backgroundColor: "#e3f2fd",
                        color: "#1565c0",
                        fontWeight: 600,
                        cursor: "pointer",
                        border:
                          colorFilter === "Blue" ? "2px solid #1565c0" : "none",
                        "&:hover": { opacity: 0.8 },
                      }}
                    />
                    <Chip
                      label="Existing"
                      size="small"
                      onClick={() =>
                        setColorFilter(colorFilter === "Black" ? null : "Black")
                      }
                      sx={{
                        backgroundColor: "#fff",
                        color: "#000",
                        border:
                          colorFilter === "Black"
                            ? "2px solid #000"
                            : "1px solid #e0e0e0",
                        fontWeight: 600,
                        cursor: "pointer",
                        "&:hover": { opacity: 0.8 },
                      }}
                    />
                    <Chip
                      label="Deleted"
                      size="small"
                      onClick={() =>
                        setColorFilter(colorFilter === "Red" ? null : "Red")
                      }
                      sx={{
                        backgroundColor: "#ffebee",
                        color: "#c62828",
                        fontWeight: 600,
                        cursor: "pointer",
                        border:
                          colorFilter === "Red" ? "2px solid #c62828" : "none",
                        "&:hover": { opacity: 0.8 },
                      }}
                    />
                    <Chip
                      label="Updated"
                      size="small"
                      onClick={() =>
                        setColorFilter(
                          colorFilter === "Orange" ? null : "Orange",
                        )
                      }
                      sx={{
                        backgroundColor: "#fff3e0",
                        color: "#e65100",
                        fontWeight: 600,
                        cursor: "pointer",
                        border:
                          colorFilter === "Orange"
                            ? "2px solid #e65100"
                            : "none",
                        "&:hover": { opacity: 0.8 },
                      }}
                    />
                    {colorFilter && (
                      <Chip
                        label="Clear Filter"
                        size="small"
                        onClick={() => setColorFilter(null)}
                        sx={{
                          backgroundColor: "#f44336",
                          color: "#fff",
                          fontWeight: 600,
                          cursor: "pointer",
                          "&:hover": { backgroundColor: "#d32f2f" },
                        }}
                      />
                    )}
                  </Box>
                )}

                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow
                        sx={{
                          backgroundColor: "#fafbfc",
                          "& .MuiTableCell-head": {
                            borderBottom: "2px solid #e2e8f0",
                            fontSize: "0.875rem",
                            fontWeight: 600,
                            color: "#475569",
                            py: 2,
                            textTransform: "uppercase",
                            letterSpacing: "0.025em",
                          },
                        }}
                      >
                        {(applicationStage === "18" ||
                          applicationStage === "18b" ||
                          applicationStage === "20") &&
                          (localStorage.getItem("userTypeId") === "4" ||
                            localStorage.getItem("userTypeId") === "6") && (
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={
                                  selectedItems.length ===
                                    filteredReferenceMaterials.length &&
                                  filteredReferenceMaterials.length > 0
                                }
                                indeterminate={
                                  selectedItems.length > 0 &&
                                  selectedItems.length <
                                    filteredReferenceMaterials.length
                                }
                                onChange={handleSelectAll}
                              />
                            </TableCell>
                          )}
                        <TableCell>Reference Material Name</TableCell>
                        <TableCell>Source</TableCell>
                        <TableCell>Traceability</TableCell>
                        <TableCell>Date of Expiry</TableCell>
                        <TableCell>Entity Type</TableCell>
                        {(applicationStage === "18" ||
                          applicationStage === "18a" ||
                          applicationStage === "18b" ||
                          applicationStage === "20") && (
                          <TableCell>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <CheckIcon fontSize="small" />
                              Adequacy Status
                            </Box>
                          </TableCell>
                        )}
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredReferenceMaterials.map((material) => (
                        <TableRow
                          key={material.Id}
                          sx={getRowStyling(material)}
                        >
                          {(applicationStage === "18" ||
                            applicationStage === "18b" ||
                            applicationStage === "20") &&
                            (localStorage.getItem("userTypeId") === "4" ||
                              localStorage.getItem("userTypeId") === "6") && (
                              <TableCell padding="checkbox">
                                <Checkbox
                                  checked={selectedItems.includes(material.Id)}
                                  onChange={() => handleSelectItem(material.Id)}
                                />
                              </TableCell>
                            )}
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500 }}
                            >
                              {material.Name}
                            </Typography>
                          </TableCell>
                          <TableCell>{material.Source}</TableCell>
                          <TableCell>{material.Traceability}</TableCell>
                          <TableCell>
                            {material.DateOfExpiry
                              ? new Date(
                                  material.DateOfExpiry,
                                ).toLocaleDateString() === ""
                                ? "-"
                                : new Date(
                                    material.DateOfExpiry,
                                  ).toLocaleDateString()
                              : "-"}
                          </TableCell>
                          <TableCell>{material.supportingEntity}</TableCell>
                          {(applicationStage === "18" ||
                            applicationStage === "18a" ||
                            applicationStage === "18b" ||
                            applicationStage === "20") && (
                            <TableCell>
                              {(() => {
                                const status = getMaterialStatus(material.Id);
                                return (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: 0.5,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        px: 2,
                                        py: 0.5,
                                        borderRadius: 1,
                                        fontSize: "0.75rem",
                                        fontWeight: 600,
                                        textAlign: "center",
                                        minWidth: "80px",
                                        bgcolor:
                                          status.status === "Adequate"
                                            ? "#e8f5e8"
                                            : status.status === "Inadequate"
                                              ? "#ffebee"
                                              : "#f5f5f5",
                                        color:
                                          status.status === "Adequate"
                                            ? "#2e7d32"
                                            : status.status === "Inadequate"
                                              ? "#c62828"
                                              : "#666",
                                        border: `1px solid ${
                                          status.status === "Adequate"
                                            ? "#a5d6a7"
                                            : status.status === "Inadequate"
                                              ? "#ef9a9a"
                                              : "#ddd"
                                        }`,
                                      }}
                                    >
                                      {status.status || "Not Assessed"}
                                    </Box>
                                    {status.status === "Inadequate" &&
                                      status.remarks && (
                                        <Tooltip title={status.remarks} arrow>
                                          <Box
                                            sx={{
                                              px: 1,
                                              py: 0.5,
                                              borderRadius: 0.5,
                                              fontSize: "0.65rem",
                                              bgcolor: "#fff3e0",
                                              color: "#e65100",
                                              border: "1px solid #ffcc02",
                                              maxWidth: "150px",
                                              overflow: "hidden",
                                              textOverflow: "ellipsis",
                                              whiteSpace: "nowrap",
                                              cursor: "pointer",
                                            }}
                                          >
                                            📝 {status.remarks}
                                          </Box>
                                        </Tooltip>
                                      )}
                                    {/* Clause Number Chip for inadequate status with clause number */}
                                    {status.clauseNo &&
                                      status.status === "Inadequate" && (
                                        <Chip
                                          label={`Clause: ${status.clauseNo}`}
                                          size="small"
                                          sx={{
                                            mt: 0.5,
                                            height: "20px",
                                            fontSize: "0.65rem",
                                            backgroundColor: "#ffebee",
                                            color: "#d32f2f",
                                            border: "1px solid #ffcdd2",
                                            "& .MuiChip-label": {
                                              px: 0.5,
                                              fontWeight: 500,
                                            },
                                          }}
                                        />
                                      )}
                                  </Box>
                                );
                              })()}
                            </TableCell>
                          )}
                          <TableCell align="center">
                            {applicationStage === null ||
                            applicationStage === "" ||
                            applicationStage === "18a" ||
                            isReferenceMaterialSectionUnfrozen ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "center",
                                  gap: 1,
                                }}
                              >
                                <IconButton
                                  size="small"
                                  onClick={() => handleEdit(material)}
                                  title="Edit"
                                  sx={{
                                    backgroundColor: "#e3f2fd",
                                    color: "#1976d2",
                                    "&:hover": {
                                      backgroundColor: "#bbdefb",
                                    },
                                  }}
                                >
                                  <EditIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDelete(material)}
                                  title="Delete"
                                  sx={{
                                    backgroundColor: "#ffebee",
                                    color: "#d32f2f",
                                    "&:hover": {
                                      backgroundColor: "#ffcdd2",
                                    },
                                  }}
                                >
                                  <DeleteIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Box>
                            ) : (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ fontSize: "0.75rem" }}
                              >
                                View Only
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </Paper>
        )}

        {activeTab === 2 && (
          <Paper
            sx={{
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                py: 3,
                px: 4,
                background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                borderBottom: "1px solid rgba(0,0,0,0.06)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: "#334155",
                      fontSize: "1.125rem",
                    }}
                  >
                    📤 Bulk Upload Reference Materials
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#64748b", mt: 1 }}>
                    Upload an Excel file (.xlsx, .xls) or CSV file with
                    reference material data
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={downloadEmptyTemplate}
                    size="small"
                    sx={{
                      borderColor: "#10b981",
                      color: "#10b981",
                      "&:hover": {
                        borderColor: "#059669",
                        backgroundColor: "#ecfdf5",
                        color: "#059669",
                      },
                    }}
                  >
                    Empty Template
                  </Button>
                  {referenceMaterials.length > 0 && (
                    <Button
                      variant="outlined"
                      startIcon={<GetAppIcon />}
                      onClick={downloadExistingDataTemplate}
                      size="small"
                      sx={{
                        borderColor: "#3b82f6",
                        color: "#3b82f6",
                        "&:hover": {
                          borderColor: "#2563eb",
                          backgroundColor: "#eff6ff",
                          color: "#2563eb",
                        },
                      }}
                    >
                      Existing Data ({referenceMaterials.length})
                    </Button>
                  )}
                </Box>
              </Box>
            </Box>

            <Box sx={{ p: 4 }}>
              <Card
                sx={{
                  border: "2px dashed #cbd5e1",
                  backgroundColor: "#f8fafc",
                  "&:hover": {
                    borderColor: "#94a3b8",
                    backgroundColor: "#f1f5f9",
                  },
                }}
              >
                <CardContent sx={{ textAlign: "center", py: 4 }}>
                  <input
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleFileUpload}
                    style={{ display: "none" }}
                    id="bulk-upload-input"
                  />
                  <label htmlFor="bulk-upload-input">
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        cursor: "pointer",
                      }}
                    >
                      <CloudUploadIcon
                        sx={{
                          fontSize: 48,
                          color: "#64748b",
                          mb: 2,
                        }}
                      />
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: "#334155",
                          mb: 1,
                        }}
                      >
                        Choose File to Upload
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#64748b",
                          mb: 2,
                        }}
                      >
                        Drag and drop or click to select your file
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<UploadIcon />}
                        sx={{
                          backgroundColor: "#2563eb",
                          "&:hover": {
                            backgroundColor: "#1d4ed8",
                          },
                        }}
                      >
                        Browse Files
                      </Button>
                    </Box>
                  </label>
                </CardContent>
              </Card>

              {uploadFile && (
                <Box sx={{ mt: 3 }}>
                  <Card
                    sx={{
                      backgroundColor: "#f0f9ff",
                      border: "1px solid #0ea5e9",
                    }}
                  >
                    <CardContent>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              backgroundColor: "#0ea5e9",
                              borderRadius: 1,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              mr: 2,
                            }}
                          >
                            <Typography
                              sx={{ color: "white", fontWeight: "bold" }}
                            >
                              {uploadFile.name.split(".").pop().toUpperCase()}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography
                              variant="subtitle1"
                              sx={{ fontWeight: 600 }}
                            >
                              {uploadFile.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {(uploadFile.size / 1024).toFixed(1)} KB
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Button
                            variant="contained"
                            onClick={handlePreviewData}
                            sx={{
                              backgroundColor: "#16a34a",
                              "&:hover": {
                                backgroundColor: "#15803d",
                              },
                            }}
                          >
                            Preview Data
                          </Button>
                          <IconButton
                            onClick={() => setUploadFile(null)}
                            sx={{ color: "#dc2626" }}
                          >
                            <CloseIcon />
                          </IconButton>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
              )}

              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  File Format Requirements:
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                    gap: 2,
                  }}
                >
                  <Card
                    sx={{
                      backgroundColor: "#fefce8",
                      border: "1px solid #eab308",
                    }}
                  >
                    <CardContent>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 600, mb: 2 }}
                      >
                        Required Columns:
                      </Typography>
                      <Typography variant="body2" component="div">
                        • <strong>Reference Material Name</strong> - Name of the
                        reference material
                        <br />• <strong>Source</strong> - Source or supplier
                        <br />• <strong>Source ID</strong> - Source identifier
                        (optional)
                        <br />• <strong>Traceability</strong> - Traceability
                        information
                        <br />• <strong>Date of Expiry</strong> - Expiry date
                        (YYYY-MM-DD format)
                      </Typography>
                    </CardContent>
                  </Card>
                  <Card
                    sx={{
                      backgroundColor: "#f0f9ff",
                      border: "1px solid #0ea5e9",
                    }}
                  >
                    <CardContent>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 600, mb: 2 }}
                      >
                        Format Notes:
                      </Typography>
                      <Typography variant="body2" component="div">
                        • Headers should be in the first row
                        <br />
                        • Date format: YYYY-MM-DD (e.g., 2024-12-31)
                        <br />
                        • All fields except Source ID are required
                        <br />• Maximum 1000 rows per file
                      </Typography>
                    </CardContent>
                  </Card>
                  <Card
                    sx={{
                      backgroundColor: "#f0fdf4",
                      border: "1px solid #22c55e",
                    }}
                  >
                    <CardContent>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 600, mb: 2 }}
                      >
                        📥 Download Templates:
                      </Typography>
                      <Typography variant="body2" component="div">
                        • <strong>Empty Template</strong> - Get a template with
                        sample data
                        <br />• <strong>Existing Data</strong> - Download
                        current reference materials
                        <br />
                        • Use downloaded templates as reference for format
                        <br />• Edit and re-upload the existing data template
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>
              </Box>
            </Box>
          </Paper>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={cancelDelete}
          aria-labelledby="delete-dialog-title"
        >
          <DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete the reference material "
              {materialToDelete?.Name}"? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={cancelDelete} color="primary">
              Cancel
            </Button>
            <Button onClick={confirmDelete} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Bulk Upload Preview Modal */}
        <Dialog
          open={previewModalOpen}
          onClose={handlePreviewModalClose}
          maxWidth="xl"
          fullWidth
          PaperProps={{
            sx: { height: "90vh" },
          }}
        >
          <DialogTitle
            sx={{
              backgroundColor: "#f8fafc",
              borderBottom: "1px solid #e2e8f0",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                📋 Preview Bulk Upload Data
              </Typography>
              <Typography variant="body2" color="text.secondary">
                File: {previewFileName} ({editedPreviewData.length} rows)
              </Typography>
            </Box>
            <IconButton onClick={handlePreviewModalClose}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ p: 0 }}>
            {validationErrors.length > 0 && (
              <Box
                sx={{
                  p: 2,
                  backgroundColor: "#fef2f2",
                  borderBottom: "1px solid #fecaca",
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ color: "#dc2626", fontWeight: 600, mb: 1 }}
                >
                  ⚠️ Validation Errors ({validationErrors.length})
                </Typography>
                <Typography variant="body2" sx={{ color: "#dc2626" }}>
                  Please fix the highlighted errors before saving.
                </Typography>
              </Box>
            )}

            <TableContainer sx={{ height: "calc(90vh - 200px)" }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        backgroundColor: "#f1f5f9",
                        fontWeight: 600,
                        minWidth: 50,
                      }}
                    >
                      #
                    </TableCell>
                    {previewHeaders.map((header) => (
                      <TableCell
                        key={header}
                        sx={{
                          backgroundColor: "#f1f5f9",
                          fontWeight: 600,
                          minWidth: 200,
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {editedPreviewData.map((row, rowIndex) => (
                    <TableRow key={rowIndex}>
                      <TableCell
                        sx={{ fontWeight: 600, backgroundColor: "#fafbfc" }}
                      >
                        {rowIndex + 1}
                      </TableCell>
                      {previewHeaders.map((header) => {
                        const hasError = validationErrors.some(
                          (error) =>
                            error.row === rowIndex && error.column === header,
                        );
                        const errorMessage = validationErrors.find(
                          (error) =>
                            error.row === rowIndex && error.column === header,
                        )?.message;

                        return (
                          <TableCell key={header} sx={{ p: 1 }}>
                            <FormControl
                              fullWidth
                              size="small"
                              error={hasError}
                            >
                              <TextField
                                value={row[header] || ""}
                                onChange={(e) =>
                                  handlePreviewCellEdit(
                                    rowIndex,
                                    header,
                                    e.target.value,
                                  )
                                }
                                size="small"
                                variant="outlined"
                                error={hasError}
                                helperText={hasError ? errorMessage : ""}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    backgroundColor: hasError
                                      ? "#fef2f2"
                                      : "white",
                                  },
                                }}
                              />
                            </FormControl>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </DialogContent>

          <DialogActions
            sx={{
              backgroundColor: "#f8fafc",
              borderTop: "1px solid #e2e8f0",
              p: 2,
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {hasUnsavedChanges && (
                <Typography variant="body2" sx={{ color: "#f59e0b" }}>
                  ⚠️ You have unsaved changes
                </Typography>
              )}
              {validationErrors.length > 0 && (
                <Typography variant="body2" sx={{ color: "#dc2626" }}>
                  ❌ {validationErrors.length} validation error(s)
                </Typography>
              )}
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button onClick={handlePreviewModalClose} color="inherit">
                Cancel
              </Button>
              <Button
                onClick={handleSaveBulkData}
                variant="contained"
                disabled={validationErrors.length > 0}
                sx={{
                  backgroundColor: "#16a34a",
                  "&:hover": {
                    backgroundColor: "#15803d",
                  },
                  "&:disabled": {
                    backgroundColor: "#9ca3af",
                  },
                }}
              >
                Save {editedPreviewData.length} Reference Materials
              </Button>
            </Box>
          </DialogActions>
        </Dialog>

        {/* Adequacy Check Dialog */}
        <Dialog
          open={adequacyDialogOpen}
          onClose={closeAdequacyDialog}
          maxWidth="sm"
          fullWidth
          aria-labelledby="adequacy-dialog-title"
        >
          <DialogTitle
            id="adequacy-dialog-title"
            sx={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
              fontWeight: 600,
            }}
          >
            ✅ Mark Reference Material Adequacy Status
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="body1"
                sx={{ mb: 2, color: "text.secondary" }}
              >
                Selected Reference Materials:{" "}
                <strong>{selectedItems.length}</strong>
              </Typography>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel id="adequacy-status-label">
                    Adequacy Status
                  </InputLabel>
                  <Select
                    labelId="adequacy-status-label"
                    value={adequacyStatus}
                    label="Adequacy Status"
                    onChange={(e) => setAdequacyStatus(e.target.value)}
                  >
                    <MenuItem value="Adequate">Adequate</MenuItem>
                    <MenuItem value="Inadequate">Inadequate</MenuItem>

                    {userId === "4" && (
                      <MenuItem value="To be Verify">To be Verify</MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Grid>

              {/* Clause Number dropdown for Assessment users when status is Inadequate */}
              {adequacyStatus === "Inadequate" && getUserTypeId() === 6 && (
                <Grid item xs={12}>
                  <Autocomplete
                    fullWidth
                    options={clauseNo}
                    getOptionLabel={(option) =>
                      option?.DisplayName || option?.ClauseNo || option
                    }
                    value={selectedClause}
                    onChange={(event, newValue) => {
                      console.log(
                        "📍 ReferenceMaterial - Clause selected:",
                        newValue,
                      );
                      setSelectedClause(newValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="📋 Select Clause Number"
                        variant="outlined"
                        required
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "white",
                            borderRadius: 2,
                            "&:hover": {
                              boxShadow: "0 2px 8px rgba(0,123,255,0.15)",
                            },
                            "&.Mui-focused": {
                              boxShadow: "0 4px 12px rgba(0,123,255,0.25)",
                            },
                          },
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {option?.ClauseNo || option?.DisplayName || option}
                          </Typography>
                          {option?.DisplayName && option?.ClauseNo && (
                            <Typography
                              variant="caption"
                              sx={{ color: "text.secondary" }}
                            >
                              {option.DisplayName}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    )}
                  />
                </Grid>
              )}

              {adequacyStatus === "Inadequate" && (
                <Grid item xs={12}>
                  {/* Simple textarea for remarks */}
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Remarks (Required)"
                    placeholder="Type your detailed remarks here. Use the dropdown below to select predefined remarks."
                    value={adequacyRemarks || ""}
                    onChange={(e) =>
                      handleAdequacyRemarksChange(e.target.value, false)
                    }
                    required
                    variant="outlined"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "white",
                        borderRadius: 2,
                        "&:hover": {
                          boxShadow: "0 2px 8px rgba(220,53,69,0.15)",
                        },
                        "&.Mui-focused": {
                          boxShadow: "0 4px 12px rgba(220,53,69,0.25)",
                        },
                      },
                    }}
                  />

                  {/* Predefined Remarks Dropdown */}
                  {scrutinyRemarks.length > 0 && (
                    <Box sx={{ mt: 2, position: "relative" }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() =>
                          setIsCheckboxDropdownOpen(!isCheckboxDropdownOpen)
                        }
                        sx={{
                          mb: 1,
                          borderColor: "#007bff",
                          color: "#007bff",
                          "&:hover": {
                            borderColor: "#0056b3",
                            backgroundColor: "#f8f9ff",
                          },
                        }}
                      >
                        {isCheckboxDropdownOpen ? "Hide" : "Show"} Predefined
                        Remarks ({scrutinyRemarks.length} available)
                      </Button>

                      {selectedRemarksOptions.length > 0 && (
                        <Button
                          variant="text"
                          size="small"
                          onClick={handleClearAllCheckboxes}
                          sx={{ ml: 1, color: "#dc3545" }}
                        >
                          Clear All ({selectedRemarksOptions.length})
                        </Button>
                      )}

                      {isCheckboxDropdownOpen && (
                        <Paper
                          elevation={3}
                          sx={{
                            mt: 1,
                            maxHeight: 300,
                            overflow: "auto",
                            border: "1px solid #e0e0e0",
                            borderRadius: 2,
                          }}
                        >
                          <MenuList dense>
                            {scrutinyRemarks.map((option, index) => {
                              const remarkText =
                                typeof option === "string"
                                  ? option
                                  : option.inadequate_remarks;
                              const isSelected = selectedRemarksOptions.some(
                                (item) =>
                                  (typeof item === "string"
                                    ? item
                                    : item.inadequate_remarks) === remarkText,
                              );

                              return (
                                <MenuItem
                                  key={index}
                                  onClick={() =>
                                    handleCheckboxChange(option, !isSelected)
                                  }
                                  sx={{
                                    py: 1,
                                    px: 2,
                                    display: "flex",
                                    alignItems: "flex-start",
                                    minHeight: "auto",
                                  }}
                                >
                                  <Checkbox
                                    checked={isSelected}
                                    size="small"
                                    sx={{ p: 0, mr: 2 }}
                                  />
                                  <Box sx={{ flexGrow: 1 }}>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontSize: "0.875rem",
                                        lineHeight: 1.4,
                                        wordBreak: "break-word",
                                        whiteSpace: "pre-wrap",
                                      }}
                                    >
                                      {remarkText}
                                    </Typography>
                                    {typeof option !== "string" &&
                                      option.type && (
                                        <Chip
                                          label={option.type}
                                          size="small"
                                          color={
                                            option.type === "Assessment"
                                              ? "primary"
                                              : "secondary"
                                          }
                                          variant="outlined"
                                          sx={{
                                            fontSize: "0.7rem",
                                            height: "18px",
                                            mt: 0.5,
                                          }}
                                        />
                                      )}
                                  </Box>
                                </MenuItem>
                              );
                            })}
                          </MenuList>
                        </Paper>
                      )}
                    </Box>
                  )}

                  <Typography
                    variant="caption"
                    sx={{
                      color: "#6c757d",
                      display: "block",
                      mt: 1,
                      fontStyle: "italic",
                    }}
                  >
                    {loadingRemarks
                      ? "Loading existing remarks..."
                      : scrutinyRemarks.length > 0
                        ? `${scrutinyRemarks.length} predefined remark(s) available for "Reference Material". You can select multiple options from the dropdown above.`
                        : "Type your detailed remarks in the text area above."}
                  </Typography>
                </Grid>
              )}

              {adequacyStatus === "Adequate" && (
                <Grid item xs={12}>
                  <Alert severity="success">
                    <Typography variant="body2">
                      ✅ Selected reference materials will be marked as{" "}
                      <strong>Adequate</strong>.
                    </Typography>
                  </Alert>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3, gap: 1 }}>
            <Button
              onClick={closeAdequacyDialog}
              color="primary"
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAdequacySubmit}
              color="success"
              variant="contained"
              disabled={loading || !adequacyStatus}
              startIcon={loading ? <CircularProgress size={20} /> : null}
              sx={{
                px: 3,
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              {loading ? "Updating..." : "Submit Adequacy"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Notification Snackbar */}
        <Snackbar
          open={notification.open}
          autoHideDuration={6000}
          onClose={closeNotification}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={closeNotification}
            severity={notification.severity}
            sx={{ width: "100%" }}
          >
            {notification.message}
          </Alert>
        </Snackbar>
      </Box>
    );
};

export default ReferenceMaterial;